import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { buildFilesUrl } from '../config/api';

interface FileItem {
  key: string;
  size: number;
  uploaded: string;
  metadata: {
    filename?: string;
    contentType?: string;
    isPublic?: string | boolean;
    userId?: string;
    uploadedAt?: string;
  };
}

interface FileManagerProps {
  onNarrative?: (message: string, type: 'info' | 'success' | 'error') => void;
  refreshTrigger?: number;
}

export const FileManager: React.FC<FileManagerProps> = ({ onNarrative, refreshTrigger }) => {
  const { token } = useAuth();
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);

  const FILES_URL = buildFilesUrl('');

  // Load user files
  const loadFiles = async () => {
    if (!token) return;

    setLoading(true);
    onNarrative?.('📂 Loading your files from R2...', 'info');

    try {
      const response = await fetch(`${FILES_URL}/files`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load files');
      }

      const data = await response.json();
      setFiles(data.files || []);
      onNarrative?.(`✅ Loaded ${data.count} files from R2 storage`, 'success');
    } catch (error) {
      onNarrative?.(
        `❌ Failed to load files: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();
  }, [token, refreshTrigger]);

  const handleDelete = async (key: string) => {
    if (!confirm('Are you sure you want to delete this file?')) return;

    onNarrative?.(`🗑️ Deleting file: ${key}`, 'info');

    try {
      const response = await fetch(`${FILES_URL}/files/${encodeURIComponent(key)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete file');
      }

      onNarrative?.('✅ File deleted successfully!', 'success');
      loadFiles(); // Reload the list
    } catch (error) {
      onNarrative?.(
        `❌ Delete failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'error'
      );
    }
  };

  const handleDownload = (file: FileItem) => {
    const url = `${FILES_URL}/files/${encodeURIComponent(file.key)}`;
    const isPublic = file.metadata?.isPublic === true || file.metadata?.isPublic === 'true';
    
    if (isPublic) {
      // Public files can be downloaded directly
      window.open(url, '_blank');
      onNarrative?.(`📥 Downloading public file: ${file.metadata?.filename || file.key}`, 'info');
    } else {
      // Private files need auth header, so we'll fetch and create blob
      onNarrative?.(`🔐 Downloading private file: ${file.metadata?.filename || file.key}`, 'info');
      
      fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
        .then(res => res.blob())
        .then(blob => {
          const blobUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = file.metadata?.filename || 'download';
          a.click();
          window.URL.revokeObjectURL(blobUrl);
          onNarrative?.('✅ Download complete!', 'success');
        })
        .catch(error => {
          onNarrative?.(`❌ Download failed: ${error.message}`, 'error');
        });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">📁 R2 File Manager</h2>
        <button
          onClick={loadFiles}
          disabled={loading}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition-colors"
        >
          {loading ? '⌛ Loading...' : '🔄 Refresh'}
        </button>
      </div>

      {files.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p className="text-lg">No files uploaded yet</p>
          <p className="text-sm mt-2">Upload files using the R2 File Upload panel</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  File
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Access
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Uploaded
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {files.map((file) => (
                <tr 
                  key={file.key}
                  className={`hover:bg-gray-50 cursor-pointer ${
                    selectedFile === file.key ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => {
                    setSelectedFile(file.key);
                    if (file.metadata?.contentType?.startsWith('image/')) {
                      setPreviewFile(file);
                      onNarrative?.('🖼️ Opening image preview...', 'info');
                    }
                  }}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="mr-2">
                        {file.metadata?.contentType?.startsWith('image/') ? '🖼️' : '📄'}
                      </span>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {file.metadata?.filename || 'Unknown'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {file.key}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatFileSize(file.size)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {file.metadata?.contentType || 'Unknown'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      (file.metadata?.isPublic === true || file.metadata?.isPublic === 'true')
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {(file.metadata?.isPublic === true || file.metadata?.isPublic === 'true') ? '🌐 Public' : '🔒 Private'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(file.uploaded)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(file);
                      }}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      📥 Download
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(file.key);
                      }}
                      className="text-red-600 hover:text-red-900"
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewFile && previewFile.metadata?.contentType?.startsWith('image/') && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewFile(null)}
        >
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">{previewFile.metadata.filename}</h3>
              <button
                onClick={() => setPreviewFile(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              {(previewFile.metadata?.isPublic === true || previewFile.metadata?.isPublic === 'true') ? (
                <img
                  src={`${FILES_URL}/files/${encodeURIComponent(previewFile.key)}`}
                  alt={previewFile.metadata.filename}
                  className="max-w-full max-h-[70vh] object-contain"
                  onError={(e) => {
                    onNarrative?.('❌ Failed to load image preview', 'error');
                    setPreviewFile(null);
                  }}
                />
              ) : (
                <div className="text-center p-8">
                  <div className="mb-6">
                    <span className="text-6xl">🔒</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    Private File Protection
                  </h3>
                  <p className="text-gray-600 mb-6">
                    This file is private and requires authentication to view. 
                    Private files are protected from direct browser access for security.
                  </p>
                  <div className="space-y-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(previewFile);
                      }}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                    >
                      📥 Download File
                    </button>
                    <p className="text-sm text-gray-500">
                      File: {previewFile.metadata?.filename || 'Unknown'}
                    </p>
                    <p className="text-xs text-gray-400">
                      Size: {formatFileSize(previewFile.size)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 text-sm text-gray-600">
        <p>💡 Files are stored in CloudFlare R2 with automatic global replication</p>
        <p className="mt-1">🔐 Private files require authentication to access</p>
        <p className="mt-1">🖼️ Click on image files to preview</p>
      </div>
    </div>
  );
};